import React from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { CheckCircle2, MessageCircle, Sparkles, Gift, Users, ShieldCheck, HeartHandshake, TrendingUp, PlaneTakeoff, ArrowRight, FileText, Phone } from "lucide-react";
import api from "@/lib/api";
import { buildWhatsAppUrl } from "@/lib/whatsapp";
import { useTheme } from "@/context/ThemeContext";

const ROLE_ORDER = ["admin", "cs", "marketing"];
const COUNTRY_LABEL = { id: { en: "Indonesia", id: "Indonesia" }, bn: { en: "Brunei", id: "Brunei" } };

function buildWaLink(phone, msg) {
  return buildWhatsAppUrl(phone, msg);
}

function ContactCard({ c, lang, message, t }) {
  const label = lang === "id" ? c.label_id : c.label_en;
  const countryLabel = COUNTRY_LABEL[c.country]?.[lang] || c.country?.toUpperCase() || "";
  const href = buildWaLink(c.phone, message);
  const roleTint = {
    admin: "border-[color:var(--brand-primary,#b8860b)]",
    cs: "border-emerald-400",
    marketing: "border-sky-400",
  }[c.role] || "border-zinc-300";
  return (
    <a
      href={href}
      target="_blank" rel="noopener noreferrer"
      className={`group bg-white border-2 ${roleTint} rounded-3xl p-6 card-lift block relative overflow-hidden`}
      data-testid={`join-contact-${c.key}`}
    >
      <div className="absolute -top-8 -right-8 w-32 h-32 rounded-full opacity-10 group-hover:opacity-20 transition-opacity"
           style={{ background: "var(--brand-primary, #b8860b)" }} />
      <div className="flex items-start justify-between mb-4 relative">
        <div>
          <div className="text-[10px] uppercase tracking-[0.24em] mb-2" style={{color: "var(--brand-primary, #b8860b)"}}>
            {countryLabel} · {c.role}
          </div>
          <div className="font-serif text-xl text-zinc-950 leading-snug">{label}</div>
          <div className="mt-1 text-sm font-mono text-zinc-900">{c.phone}</div>
        </div>
        <div className="w-12 h-12 rounded-2xl bg-emerald-50 flex items-center justify-center flex-shrink-0">
          <MessageCircle className="text-emerald-600" size={22} />
        </div>
      </div>
      <div className="flex items-center justify-between mt-6 pt-4 border-t border-zinc-100 relative">
        <span className="text-xs text-zinc-800">{t("join.contact_wa_button", "Chat on WhatsApp")}</span>
        <ArrowRight size={16} className="text-zinc-400 group-hover:translate-x-1 group-hover:text-emerald-600 transition-all" />
      </div>
    </a>
  );
}

export default function Join() {
  const { t, i18n } = useTranslation();
  const { settings } = useTheme();
  const { data: publicSettings } = useQuery({
    queryKey: ["settings-public"],
    queryFn: async () => (await api.get("/settings/public")).data,
  });
  const contacts = publicSettings?.contacts || settings?.contacts || [];
  const message = t("join.contact_default_message", "Hello Adelina, I would like to register as a candidate.");
  const lang = i18n.language || "en";

  // Group contacts by role for a clean layout
  const grouped = ROLE_ORDER.map((role) => ({
    role,
    items: contacts.filter((c) => c.role === role),
  })).filter((g) => g.items.length > 0);

  const benefits = [
    { icon: Gift,           k: "b1" },
    { icon: ShieldCheck,    k: "b2" },
    { icon: TrendingUp,     k: "b3" },
    { icon: HeartHandshake, k: "b4" },
    { icon: Sparkles,       k: "b5" },
    { icon: PlaneTakeoff,   k: "b6" },
  ];

  const primaryContact = contacts.find((c) => c.role === "marketing" && c.country === "id") || contacts[0];
  const primaryHref = primaryContact ? buildWaLink(primaryContact.phone, message) : "#";

  return (
    <div className="bg-white">
      {/* HERO */}
      <section className="relative bg-zinc-950 text-white overflow-hidden grain">
        <div className="absolute inset-0 opacity-20"
             style={{ background: "radial-gradient(80% 60% at 30% 30%, var(--brand-primary,#b8860b) 0%, transparent 60%)" }} />
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24 lg:py-32 grid grid-cols-1 lg:grid-cols-12 gap-10 relative">
          <div className="lg:col-span-7 fade-up">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-white/20 text-xs tracking-widest uppercase mb-6" style={{color: "var(--brand-accent, #d4a017)"}}>
              <Sparkles size={12} /> {t("join.overline", "Become an Adelina Candidate")}
            </div>
            <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl leading-[1.05] tracking-tight" data-testid="join-hero-title">
              {t("join.title", "Your dream job abroad starts here.")}
            </h1>
            <p className="mt-8 text-lg md:text-xl text-zinc-300 leading-relaxed max-w-2xl">
              {t("join.subtitle")}
            </p>
            <div className="mt-10 flex flex-wrap items-center gap-4">
              <a
                href={primaryHref}
                target="_blank" rel="noopener noreferrer"
                className="btn-gold inline-flex items-center gap-2"
                data-testid="join-hero-cta-primary"
              >
                <MessageCircle size={18} /> {t("join.cta_primary", "Chat with our recruiter")}
              </a>
              <a href="#contacts" className="px-7 py-3 rounded-full border border-white/40 text-white hover:bg-white hover:text-zinc-950 transition-colors text-sm font-semibold">
                {t("join.cta_secondary", "See what we offer")}
              </a>
            </div>
            {/* Trust markers */}
            <div className="mt-14 flex flex-wrap items-center gap-8 text-sm text-zinc-400">
              <div className="flex items-center gap-2"><Users size={14} className="text-emerald-400"/>4,200+ {t("stats.placed", "placed")}</div>
              <div className="flex items-center gap-2"><ShieldCheck size={14} className="text-emerald-400"/>100% Legal &amp; Documented</div>
              <div className="flex items-center gap-2"><Gift size={14} className="text-emerald-400"/>{t("join.b1_title", "Zero Placement Fees")}</div>
            </div>
          </div>

          {/* Right - illustration/photo card */}
          <div className="lg:col-span-5 relative">
            <div className="relative bg-gradient-to-br from-zinc-900 via-zinc-800 to-zinc-950 border border-white/10 rounded-3xl p-8 shadow-2xl">
              <div className="text-[10px] tracking-widest uppercase mb-3" style={{color: "var(--brand-accent, #d4a017)"}}>{t("join.opportunities", "Today's Opportunities")}</div>
              <div className="space-y-3">
                {[
                  { flag: "🇧🇳", role: "Amah / Domestic Helper", location: "Brunei Darussalam" },
                  { flag: "🇸🇬", role: "Caregiver — Elderly",    location: "Singapore" },
                  { flag: "🇹🇼", role: "Manufacturing Operator", location: "Taiwan" },
                  { flag: "🇲🇾", role: "Waiter / Hospitality",   location: "Malaysia" },
                ].map((row) => (
                  <div key={row.role} className="flex items-center gap-4 py-3 border-b border-white/10 last:border-none">
                    <div className="text-2xl">{row.flag}</div>
                    <div className="flex-1">
                      <div className="text-sm font-semibold text-white">{row.role}</div>
                      <div className="text-xs text-zinc-400">{row.location}</div>
                    </div>
                    <CheckCircle2 size={16} className="text-emerald-400" />
                  </div>
                ))}
              </div>
              <div className="mt-6 pt-6 border-t border-white/10 text-xs text-zinc-400 flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                <span>{t("join.contacts_desc", "Recruiters answer within a few minutes")}</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* BENEFITS */}
      <section className="py-24 px-6 lg:px-10 bg-zinc-50">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-10 items-end mb-14">
            <div>
              <div className="overline mb-3" data-testid="join-benefits-overline">{t("join.benefits_overline", "Why join Adelina")}</div>
              <h2 className="font-serif text-4xl md:text-5xl text-zinc-950 tracking-tight">{t("join.benefits_title")}</h2>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {benefits.map((b) => {
              const Icon = b.icon;
              return (
                <div key={b.k} className="bg-white border border-zinc-200 rounded-3xl p-8 card-lift">
                  <div className="w-12 h-12 rounded-2xl flex items-center justify-center mb-5"
                       style={{ background: "color-mix(in srgb, var(--brand-primary,#b8860b) 12%, transparent)" }}>
                    <Icon style={{color: "var(--brand-primary, #b8860b)"}} size={22} />
                  </div>
                  <div className="font-serif text-2xl text-zinc-950 mb-2">{t(`join.${b.k}_title`)}</div>
                  <p className="text-zinc-900 text-sm leading-relaxed">{t(`join.${b.k}_desc`)}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="py-24 px-6 lg:px-10 bg-zinc-950 text-white relative overflow-hidden grain">
        <div className="max-w-7xl mx-auto relative">
          <div className="overline mb-3" style={{color: "var(--brand-accent, #d4a017)"}}>{t("join.step_overline", "How it works")}</div>
          <h2 className="font-serif text-4xl md:text-5xl tracking-tight max-w-2xl mb-14">{t("join.step_title")}</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { n: "01", icon: MessageCircle, k: "step1" },
              { n: "02", icon: FileText,      k: "step2" },
              { n: "03", icon: PlaneTakeoff,  k: "step3" },
            ].map((s) => {
              const Icon = s.icon;
              return (
                <div key={s.n} className="border-t border-zinc-800 pt-6">
                  <div className="flex items-center justify-between mb-4">
                    <div style={{color: "var(--brand-accent, #d4a017)"}} className="font-serif text-3xl">{s.n}</div>
                    <Icon style={{color: "var(--brand-accent, #d4a017)"}} size={22} />
                  </div>
                  <div className="text-xl mb-2">{t(`join.${s.k}_t`)}</div>
                  <p className="text-zinc-400 text-sm leading-relaxed">{t(`join.${s.k}_d`)}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* PROMO REFERRAL */}
      <section className="py-20 px-6 lg:px-10">
        <div className="max-w-6xl mx-auto rounded-3xl p-10 md:p-14 relative overflow-hidden"
             style={{ background: "linear-gradient(120deg, var(--brand-dark,#0a0a0a) 0%, #1c1c22 100%)" }}>
          <div className="absolute -right-16 -top-16 w-64 h-64 rounded-full opacity-20"
               style={{ background: "var(--brand-primary,#b8860b)" }} />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-10 items-center relative">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-white/20 text-[10px] tracking-widest uppercase mb-4" style={{color: "var(--brand-accent, #d4a017)"}}>
                <Gift size={11} /> {t("join.promo_overline", "Bonus for referrals")}
              </div>
              <h3 className="font-serif text-3xl md:text-4xl text-white leading-tight">{t("join.promo_title")}</h3>
              <p className="mt-4 text-zinc-300 leading-relaxed max-w-xl">{t("join.promo_desc")}</p>
            </div>
            <div className="flex md:justify-end">
              <a href={primaryHref} target="_blank" rel="noopener noreferrer" className="btn-gold inline-flex items-center gap-2" data-testid="join-promo-cta">
                <MessageCircle size={18} /> {t("join.cta_primary", "Chat with our recruiter")}
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* CONTACTS GRID */}
      <section id="contacts" className="py-24 px-6 lg:px-10">
        <div className="max-w-7xl mx-auto">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <div className="overline mb-3">{t("join.contacts_overline", "Speak with Adelina")}</div>
            <h2 className="font-serif text-4xl md:text-5xl text-zinc-950 tracking-tight">{t("join.contacts_title")}</h2>
            <p className="mt-4 text-zinc-900 leading-relaxed">{t("join.contacts_desc")}</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {contacts.map((c) => (
              <ContactCard key={c.key} c={c} lang={lang} message={message} t={t} />
            ))}
          </div>
          <div className="mt-10 text-center text-sm text-zinc-800">
            <Link to="/contact" className="underline" style={{color: "var(--brand-primary,#b8860b)"}}>Have questions? Contact our team.</Link>
          </div>
        </div>
      </section>
    </div>
  );
}
